import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Plane, MapPin, Calendar, Heart, Brain, CheckCircle, ArrowRight } from 'lucide-react';
import CTA from '../components/CTA';

const HolidayAnxiety: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Holiday & Travel Anxiety Hypnotherapy | Causeway Coast</title>
        <meta name="description" content="Pre-trip anxiety, travel stress, holiday overwhelm? Solution-focused hypnotherapy helps you prepare mentally for vacations, family visits, and travel. Serving Portrush, Ballycastle, Bushmills & across Northern Ireland." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/holiday-anxiety/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How does hypnotherapy help with travel anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy helps with travel anxiety by working with the subconscious mind to reduce the fight-or-flight response associated with travel. It helps reframe anxious thoughts about flying, ferries, or being away from home, replacing them with calm, confident mental patterns. Many clients find hypnotherapy particularly effective for pre-trip preparation, creating a sense of control and calm before departure."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with fear of flying?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can be highly effective for fear of flying. It addresses the subconscious triggers that cause flight anxiety—such as fear of loss of control, turbulence, or enclosed spaces—and helps create new, calmer associations with air travel. Pre-flight preparation sessions can significantly reduce anxiety levels before your journey."
                }
              },
              {
                "@type": "Question",
                "name": "What about family visit stress and holiday overwhelm?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy can help manage the stress of family visits, holiday planning overwhelm, and social anxiety during vacations. It helps you set mental boundaries, manage expectations, and approach family dynamics with greater calm and resilience. Sessions can address specific triggers like hosting responsibilities, travel logistics, or relationship tension during holidays."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions do I need before a trip?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients benefit from 2-4 pre-trip hypnotherapy sessions for travel anxiety. Ideally, start 4-6 weeks before departure to allow time for the new mental patterns to establish. For specific phobias like fear of flying, you may benefit from more sessions. We'll discuss your timeline and goals during the free 10-minute consultation."
                }
              },
              {
                "@type": "Question",
                "name": "Can I have sessions before ferry travel from Ballycastle?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Many Ballycastle residents and visitors use hypnotherapy to manage ferry anxiety before the Rathlin Island crossing or longer ferry journeys. Sessions can address seasickness anxiety, fear of water, or general travel nerves. Online sessions are particularly convenient if you're already in Ballycastle preparing for your ferry departure."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for vacation planning anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can help reduce the overwhelm and decision fatigue associated with vacation planning. It helps calm the anxious mind that worries about every detail, builds confidence in your planning abilities, and helps you approach holiday preparation with a clearer, calmer mindset."
                }
              },
              {
                "@type": "Question",
                "name": "What if I have anxiety about being away from home?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is very effective for separation anxiety and fear of being away from home. It helps build mental resilience and confidence in your ability to cope in new environments, reduces attachment anxiety, and creates positive associations with travel and new experiences. This is particularly helpful for first-time travelers or those with agoraphobia."
                }
              },
              {
                "@type": "Question",
                "name": "Can you help with holiday food anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can address anxiety around holiday eating, whether related to diet concerns, food allergies, unfamiliar cuisines, or social eating situations. Sessions help reduce food-related stress, build flexible thinking around eating, and create calm associations with holiday meals and dining experiences."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy safe for pre-trip anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is completely safe when practiced by a qualified clinical hypnotherapist. It's a natural, relaxing state similar to daydreaming. You remain in control throughout and cannot be made to do anything against your will. Hypnotherapy is an excellent complement to practical travel preparation."
                }
              },
              {
                "@type": "Question",
                "name": "Can I have sessions while on holiday on the Causeway Coast?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, online sessions are available if you're holidaying in Portrush, Portstewart, Ballycastle, Bushmills or anywhere on the Causeway Coast and experiencing unexpected holiday anxiety. Sessions can help you manage stress during your stay and get the most out of your Northern Ireland experience."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Holiday and Travel Anxiety Hypnotherapy",
            "name": "Holiday & Travel Anxiety Relief Hypnotherapy",
            "description": "Professional solution-focused hypnotherapy to ease pre-trip anxiety, travel stress, family visit overwhelm, and vacation planning anxiety. Serving Portrush, Ballycastle, Bushmills, Portstewart and across Northern Ireland.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Sharon Clare Hypnotherapy",
              "telephone": "07932139245",
              "email": "Sharon@sharonclare-hypnotherapy.com",
              "url": "https://www.sharonclare-hypnotherapy.com/"
            },
            "areaServed": ["Portrush", "Portstewart", "Ballycastle", "Bushmills", "Belfast", "Lisburn", "Northern Ireland"]
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <div className="flex justify-center mb-4">
            <Plane className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">Holiday & Travel Anxiety</h1>
          <p className="text-xl font-heading text-gray-900 mb-3">Travel with Confidence, Not Stress</p>
          <p className="text-lg sm:text-xl text-gray-600 mb-6">Pre-trip anxiety, fear of flying, ferry stress, or family visit overwhelm? Hypnotherapy helps you prepare mentally for travel, vacations, and holiday experiences across the Causeway Coast and beyond.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book" className="btn-primary inline-flex items-center justify-center">
              <Calendar className="w-5 h-5 mr-2" /> Book Free 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Types of Holiday Anxiety */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">When Travel Feels Overwhelming</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Holidays and travel should be enjoyable, but for many people they trigger significant anxiety. Whether it's pre-trip nerves, fear of the journey itself, or stress about being away from home, travel anxiety can overshadow what should be relaxing experiences.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Plane className="w-5 h-5 text-blue-600 mr-2" />
                Travel Anxiety
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Fear of flying or turbulence</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ferry anxiety (Ballycastle to Rathlin, longer crossings)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Fear of being far from home or medical help</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Panic about losing control during travel</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Anxiety in unfamiliar places or countries</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <MapPin className="w-5 h-5 text-cyan-600 mr-2" />
                Pre-Trip Stress
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Overwhelm planning trips or holidays</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Decision fatigue choosing destinations or activities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Worry about "what if" scenarios</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Sleepless nights before departure</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Anticipatory anxiety weeks before trips</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Heart className="w-5 h-5 text-rose-600 mr-2" />
                Family Visit Anxiety
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Stress hosting family during holidays</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Tension visiting relatives or in-laws</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Social anxiety at family gatherings</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Pressure to meet expectations during visits</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Boundary-setting difficulties with family</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Brain className="w-5 h-5 text-purple-600 mr-2" />
                Vacation Overwhelm
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Difficulty relaxing even on holiday</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Guilt about taking time off work</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Worry about work piling up while away</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Food anxiety or eating stress on holiday</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Post-holiday blues or return-to-work dread</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-gray-700">
              <strong className="text-gray-900">Common across the Causeway Coast:</strong> Summer tourism brings unique anxiety triggers—crowded beaches in Portrush and Portstewart, ferry crossings from Ballycastle to Rathlin Island, navigating busy heritage sites like the Giant's Causeway and Bushmills Distillery. Whether you're a visitor experiencing travel anxiety or a local overwhelmed by seasonal tourism stress, hypnotherapy can help.
            </p>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Prepares You for Travel</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Solution-focused hypnotherapy works with your subconscious mind to reduce the fight-or-flight response triggered by travel and change the anxious thought patterns that make holidays stressful. Rather than simply "pushing through" anxiety, hypnotherapy helps rewire how your brain responds to travel-related triggers.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Brain className="w-6 h-6 text-blue-600" />
                <h3 className="font-heading font-semibold text-gray-900">Reframe Travel Thoughts</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Replace catastrophic "what if" thinking with realistic, calm perspectives. Your subconscious learns to associate travel with confidence and capability rather than danger, helping you approach trips with a clearer, calmer mindset.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Heart className="w-6 h-6 text-rose-600" />
                <h3 className="font-heading font-semibold text-gray-900">Calm the Nervous System</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Hypnotherapy helps regulate your autonomic nervous system, reducing the physical symptoms of travel anxiety—racing heart, sweating, nausea, panic. You learn to activate your body's natural relaxation response before and during travel.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <MapPin className="w-6 h-6 text-cyan-600" />
                <h3 className="font-heading font-semibold text-gray-900">Build Mental Preparation</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Visualization techniques in hypnosis let you mentally rehearse your journey in a calm, controlled state. This pre-trip mental preparation creates familiarity and confidence, reducing anxiety when you actually travel.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="font-heading font-semibold text-gray-900 mb-4">What to Expect in Sessions</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Pre-Trip Preparation (Weeks Before Travel)</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Identify specific travel triggers (flying, ferries, unfamiliar places)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Build calm mental associations with your upcoming journey</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Practice relaxation techniques for use during travel</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Visualize successful, calm travel experiences</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">During-Trip Support (If Needed)</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Online sessions available if anxiety arises during holidays</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Self-hypnosis audio recordings for on-the-go calm</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Quick grounding techniques for use in airports, ferries, or unfamiliar settings</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Tools to manage family visit stress in real-time</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Sharon's Approach to Travel Anxiety</h2>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                As a solution-focused clinical hypnotherapist, Sharon understands that travel anxiety isn't "irrational"—it's your nervous system trying to protect you. Rather than dismissing your fears, hypnotherapy works <em>with</em> your natural protective instincts to help you feel genuinely safer and more capable during travel.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Sessions focus on building mental resilience and practical coping strategies in the weeks before your trip. This preparation time is crucial—starting 4-6 weeks before departure allows the new calm mental patterns to establish, giving you genuine confidence rather than forced "positive thinking."
              </p>
              <p className="text-gray-700 leading-relaxed">
                Whether you're preparing for a Ballycastle ferry crossing, a flight from Belfast International, a family visit during the holidays, or a major international trip, hypnotherapy provides the mental preparation that makes travel feel manageable rather than overwhelming.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Why Pre-Trip Preparation Works</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Time to Rewire:</strong>
                    <p className="text-gray-700 text-sm">New neural pathways need repetition; 4-6 weeks of sessions creates lasting change, not just temporary relief.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Builds Real Confidence:</strong>
                    <p className="text-gray-700 text-sm">Mental rehearsal in hypnosis creates genuine familiarity with travel, reducing the "fear of the unknown" that drives anxiety.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Reduces Last-Minute Panic:</strong>
                    <p className="text-gray-700 text-sm">Starting early prevents the escalating anxiety spiral that happens when you wait until the week before departure.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Causeway Coast Context */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Travel Anxiety on the Causeway Coast</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Whether you're a visitor experiencing travel stress in Northern Ireland or a local managing ferry anxiety, hypnotherapy provides support across Portrush, Portstewart, Ballycastle, and Bushmills.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <a href="/portrush-hypnotherapy" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Portrush</h3>
              <p className="text-sm text-gray-700 mb-3">
                Events capital of the Causeway Coast. Pre-event anxiety support for NW200, Open Championship, and major tourism events. Online sessions available.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/portstewart-hypnotherapy" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Portstewart</h3>
              <p className="text-sm text-gray-700 mb-3">
                Genteel seaside resort. Holiday overwhelm support for visitors and seasonal tourism workers. Coastal wellness culture welcomes mental health preparation. Online sessions available (19 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/ballycastle-hypnotherapy" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Ballycastle</h3>
              <p className="text-sm text-gray-700 mb-3">
                Ferry port to Rathlin Island. Ferry anxiety, seasickness nerves, and travel preparation for island crossings and longer journeys. Working coastal town practicality. Online sessions (18 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/bushmills-hypnotherapy" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Bushmills</h3>
              <p className="text-sm text-gray-700 mb-3">
                Heritage tourism village. Support for tourism industry workers managing seasonal stress and visitor anxiety during distillery tours or heritage site visits. Online sessions available (15 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>
          </div>

          <div className="mt-8 bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <h3 className="font-heading font-semibold text-gray-900 mb-3">Clinic &amp; Online Sessions</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Sharon sees clients in person at her <strong>Belfast clinic on the Ormeau Road</strong> and at <strong>The Arzen Collective in Lisburn</strong>. For North Coast residents preparing for travel, online sessions remove the drive altogether.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Online sessions are also available and particularly convenient for travel anxiety preparation&mdash;you can schedule sessions around your travel plans, continue sessions while visiting the Causeway Coast, or access support if unexpected anxiety arises during holidays.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">How does hypnotherapy help with travel anxiety?</h3>
              <p className="text-gray-700 leading-relaxed">
                Hypnotherapy works with the subconscious mind to reduce the fight-or-flight response associated with travel. It helps reframe anxious thoughts about flying, ferries, or being away from home, replacing them with calm, confident mental patterns. Pre-trip preparation sessions create a sense of control and calm before departure.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Can hypnotherapy help with fear of flying?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, hypnotherapy can be highly effective for fear of flying. It addresses the subconscious triggers that cause flight anxiety—fear of loss of control, turbulence, or enclosed spaces—and helps create new, calmer associations with air travel. Pre-flight preparation sessions can significantly reduce anxiety levels before your journey.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">What about family visit stress and holiday overwhelm?</h3>
              <p className="text-gray-700 leading-relaxed">
                Hypnotherapy helps manage the stress of family visits, holiday planning overwhelm, and social anxiety during vacations. It helps you set mental boundaries, manage expectations, and approach family dynamics with greater calm and resilience. Sessions address specific triggers like hosting responsibilities, travel logistics, or relationship tension.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">How many sessions do I need before a trip?</h3>
              <p className="text-gray-700 leading-relaxed">
                Most clients benefit from 2-4 pre-trip sessions for travel anxiety. Ideally, start 4-6 weeks before departure to allow time for new mental patterns to establish. For specific phobias like fear of flying, you may benefit from more sessions. We'll discuss your timeline and goals during the free 10-minute consultation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Can I have sessions before ferry travel from Ballycastle?</h3>
              <p className="text-gray-700 leading-relaxed">
                Absolutely. Many Ballycastle residents and visitors use hypnotherapy to manage ferry anxiety before the Rathlin Island crossing or longer ferry journeys. Sessions can address seasickness anxiety, fear of water, or general travel nerves. Online sessions are particularly convenient if you're already in Ballycastle preparing for your ferry departure.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Is hypnotherapy safe for pre-trip anxiety?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, hypnotherapy is completely safe when practiced by a qualified clinical hypnotherapist. It's a natural, relaxing state similar to daydreaming. You remain in control throughout and cannot be made to do anything against your will. Hypnotherapy is an excellent complement to practical travel preparation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/anxiety-stress" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">General Anxiety & Stress</h3>
              <p className="text-sm text-gray-700 mb-3">
                Broader anxiety treatment including social anxiety, work stress, and panic attacks.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/medical-procedure-anxiety" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Medical Procedure Anxiety</h3>
              <p className="text-sm text-gray-700 mb-3">
                Preparation for medical procedures, scans, dental work, and healthcare appointments.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/performance-anxiety" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Performance Anxiety</h3>
              <p className="text-sm text-gray-700 mb-3">
                Event performance anxiety for sports, exams, public speaking, and competitive situations.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Prepare for Your Journey with Confidence"
        subtitle="Travel anxiety doesn't have to control your holidays. Start pre-trip hypnotherapy sessions 4-6 weeks before your departure and experience travel with calm, confidence, and genuine peace of mind."
        primaryButtonText="Book Free 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default HolidayAnxiety;
